import type { TerritorialSearchResult } from "@bonusmap/shared_ui/library";
import { BaseWebComponent } from "../base-components/base-web-component.js";
import { templateParse, templateParseToElement } from "../../logic/templateParse.js";
import { createChoicesInstance } from "../../utils/createChoicesInstance.js";
// import Choices from 'choices.js';
const { Choices } = window;


/**
 * Componente per l'input su ambito territoriale
 * sarà utilizzato per selezionare un ambito territoriale
 * e impostare o filtrare i dati in base a tale selezione.
 */
export class AmbitoInputComponent extends BaseWebComponent {
    // <ambito-input>
    static webComponentName: string = "ambito-input";
    static observedAttributes = ['readonly', 'disabled', 'value'];
    protected _templateId: string = "ambito-input";
    // valore iniziale del campo è una stringa con elementi separati da virgola
    protected initialValue: string = "";

    protected _currentDataset: TerritorialSearchResult[] = []; // Current dataset for search results

    get currentDataset(): TerritorialSearchResult[] {
        return this._currentDataset;
    }

    protected set currentDataset(newDataset: TerritorialSearchResult[]) {
        this._currentDataset = newDataset;
    }
    protected _currentSelection: TerritorialSearchResult[] = []; // Current selected territorial result
    get currentSelection(): TerritorialSearchResult[] {
        return this._currentSelection;
    }
    protected set currentSelection(newSelection: TerritorialSearchResult[]) {
        this._currentSelection = newSelection;
        // Sync value with current selection
        const ids = newSelection.map(item => item.territorial_id as string) || [];
        this.value = ids;
    }
    protected _value: string[] = [];
    get value(): string[] {
        return this._value;
    }

    protected set value(newValue: string[]) {
        this._value = newValue;
        this.valueChanged(newValue);
    }
    get readOnly(): boolean {
        return this.hasAttribute('readonly') || this.hasAttribute('disabled');
    }

    // --------------------
    // elementi del componente
    // --------------------
    protected choicesInstance!: InstanceType<Window['Choices']>; // Choice.js instance
    protected ambitoSelect!: HTMLSelectElement; // Select element for Choice.js
    protected dropdownButton!: HTMLButtonElement; // Dropdown button for search type selection
    protected currentSearchType: string = "comune"; // Current search type (regione/provincia/comune)
    // --------------------
    constructor() {
        super();
        this.initialValue = this.attributeStringOption("value", "");
        this.setGlobalListeners();

    }
    protected get initialValueArray(): string[] {
        if (!this.initialValue) return [];
        return this.initialValue.split(',').map(item => item.trim()).filter(item => item);
    }
    protected async getInitialDataset(): Promise<TerritorialSearchResult[]> {
        // Fetch initial dataset based on the initial value
        if (!this.initialValue) return [];
        const initialValueArray = this.initialValueArray;
        this.value = initialValueArray;
        if (initialValueArray.length === 0) return [];
        try {
            const result = await window.api.getTerritorialByIds(initialValueArray);
            return result.data || [];
        } catch (error) {
            console.error(`Error fetching initial territorial data: ${error}`);
            return [];
        }
    }
    protected async processInitialValue(): Promise<void> {
        // Process the initial value to create an array of strings
        if (this.initialValue && this.choicesInstance) {
            try {
                const resultData = await this.getInitialDataset();
                if (resultData && resultData.length > 0) {
                    // Create choices data for initial values
                    const choicesData = resultData.map(item => ({
                        value: item.territorial_id || '',
                        label: this.formatItemLabel(item) || '',
                        customProperties: {
                            type: item.type,
                            territorialData: item
                        }
                    }));

                    // Add choices and select them
                    this.choicesInstance.setChoices(choicesData, 'value', 'label', false);

                    // Select the initial values using the original initialValueArray
                    setTimeout(() => {
                        this.initialValueArray.forEach(id => {
                            this.choicesInstance.setChoiceByValue(id);
                        });
                        // Sync selection after all values are selected
                        this.syncSelectionFromChoices();
                    }, 100);
                }
            } catch (error) {
                console.error(`Error fetching initial territorial data: ${error}`);
            }
        }
    }
    protected setGlobalListeners(): void {
        this.on('click', '[role="load-italia"]', this.handleLoadItalia.bind(this));
        this.on('click', '[role="load-regioni"]', (e) => this.handleSetSearchType(e, 'regione'));
        this.on('click', '[role="load-province"]', (e) => this.handleSetSearchType(e, 'provincia'));
        this.on('click', '[role="load-comuni"]', (e) => this.handleSetSearchType(e, 'comune'));
        this.on('click', '[role="clear-all"]', this.handleClearAll.bind(this));
    }
    protected handleLoadItalia(e: Event): void {
        e.preventDefault();
        this.currentSearchType = 'nazione';

        // Clear only available choices, preserve existing selections
        this.choicesInstance.clearChoices();

        // Check if Italia is already selected
        if (this.value.includes("1")) {
            // Italia already selected, don't add it to choices
            this.dropdownButton.innerHTML = '<i class="bi bi-record-fill nazione"></i> Italia';
            return;
        }

        // Create Italia option
        const italiaItem: TerritorialSearchResult = {
            "territorial_id": "1",
            "territorial_code": "",
            "name": "Italia",
            "type": "nazione",
            "level_num": 0,
            "parent_id": "",
            "sigla": "",
            "region_id": "",
            "region_name": "",
            "province_id": "",
            "province_name": "",
            "province_sigla": "",
            "flag_capoluogo": true,
            "codice_belfiore": "",
            "lat": 0,
            "lon": 0
        };

        const italiaData = [{
            value: italiaItem.territorial_id || '',
            label: italiaItem.name || '',
            customProperties: {
                type: 'nazione',
                territorialData: italiaItem
            }
        }];

        this.choicesInstance.setChoices(italiaData, 'value', 'label', false);

        // Auto-select Italia
        setTimeout(() => {
            this.choicesInstance.setChoiceByValue(italiaItem.territorial_id || '');
        }, 100);

        this.dropdownButton.innerHTML = '<i class="bi bi-record-fill nazione"></i> Italia';
    }

    protected handleSetSearchType(e: Event, type: 'regione' | 'provincia' | 'comune'): void {
        e.preventDefault();
        this.currentSearchType = type;

        // Clear only available choices when search type changes, preserve selections
        this.choicesInstance.clearChoices();

        const buttonText = type === 'regione' ? 'Regioni' :
            type === 'provincia' ? 'Province' : 'Comuni';
        const iconClass = type === 'regione' ? 'regione' :
            type === 'provincia' ? 'provincia' : 'comune';
        this.dropdownButton.innerHTML = `<i class="bi bi-record-fill ${iconClass}"></i> ${buttonText}`;

        // Update placeholder based on search type
        this.choicesInstance.setChoices([]);
    }

    protected handleClearAll(e: Event): void {
        e.preventDefault();
        this.choicesInstance.clearStore();
        this.choicesInstance.clearChoices();
        this.currentSearchType = "comune";
        this.currentSelection = [];
        this.dropdownButton.innerHTML = '<i class="bi bi-record-fill comune"></i> Comuni';
    }

    protected async performSearch(searchTerm: string, searchType?: string): Promise<void> {
        if (!searchTerm || searchTerm.length == 0) {
            this.choicesInstance.clearChoices();
            return;
        }

        const type = searchType ?? this.currentSearchType;

        try {
            const params: Record<string, any> = {
                type: type,
                name: searchTerm,
                limit: 20,
                offset: 0
            };
            const result = await window.api.searchTerritorial(params);
            if (result.data && result.data.length > 0) {
                // Filter out items that are already selected
                const filteredData = result.data.filter(item =>
                    !this.value.includes(item.territorial_id || '')
                );

                this._currentDataset = filteredData;

                const choicesData = filteredData.map(item => ({
                    value: item.territorial_id || '',
                    label: this.formatItemLabel(item) || '',
                    customProperties: {
                        type: type,
                        territorialData: item
                    }
                }));

                this.choicesInstance.setChoices(choicesData, 'value', 'label', true);
            } else {
                this.choicesInstance.clearChoices();
            }
        } catch (error) {
            console.error(`Error searching territorial data:`, error);
            this.choicesInstance.clearChoices();
        }
    }

    protected formatItemLabel(item: TerritorialSearchResult): string {
        if (item.type === 'comune') {
            const province = item.province_name ? ` (${item.province_name})` : '';
            return `${item.name || ''}${province}`;
        } else if (item.type === 'provincia') {
            return item.province_name || item.name || '';
        } else if (item.type === 'regione') {
            return item.region_name || item.name || '';
        }
        return item.name || 'nazione';
    }

    protected buildRenderingData(): Record<string, any> {
        const originalData = super.buildRenderingData();
        return originalData;
    }

    protected initializeChoices(): void {
        const max = this.attributeNumberOption("max-items", 20);
        this.choicesInstance = createChoicesInstance(this.ambitoSelect, {
            removeItemButton: true,
            searchEnabled: true,
            noResultsText: 'Nessun territorio trovato',
            noChoicesText: 'Digita almeno un carattere per cercare',
            searchPlaceholderValue: 'Digita per cercare territori...',
            removeItemIconText: () => 'Rimuovi elemento',
            singleModeForMultiSelect: max == 1,
            maxItemCount: max,
            callbackOnCreateTemplates: (template) => {
                return {
                    item: (config: any, choice: any, removeItemButton: any) => {
                        // Call original template
                        const originalItem = Choices.defaults.templates.item.call(
                            this.choicesInstance, config, choice, removeItemButton
                        );

                        // Add custom class based on type
                        const customClass = choice.customProperties?.type || this.currentSearchType;
                        if (customClass) {
                            originalItem.classList.add(customClass);
                        }
                        return originalItem;
                    }
                };
            }
        });

        this.selectChangeSetupHandler();
        this.selectAddItemSetupHandler();
        this.selectRemoveItemSetupHandler();
        this.selectSearchSetupHandler();
    }
    protected selectChangeSetupHandler(): void {
        // Listen to Choice.js events  
        this.ambitoSelect.addEventListener('change', () => {
            this.syncSelectionFromChoices();
        });
    }
    protected selectAddItemSetupHandler(): void {
        // Listen to addItem and removeItem events for better synchronization
        this.ambitoSelect.addEventListener('addItem', () => {
            this.syncSelectionFromChoices();
        });
    }
    protected selectRemoveItemSetupHandler(): void {
        this.ambitoSelect.addEventListener('removeItem', () => {
            this.syncSelectionFromChoices();
        });
    }
    protected selectSearchSetupHandler(): void {
        
        // Listen to search input for live search
        this.ambitoSelect.addEventListener('search', (event: any) => {
            const searchTerm = event.detail.value;
            if (searchTerm && searchTerm.length >= 1) {
                this.performSearch(searchTerm);
            }
        });
    }
    protected syncSelectionFromChoices(): void {
        const selectedValues = this.choicesInstance.getValue(true);
        this.value = Array.isArray(selectedValues) ? selectedValues : [selectedValues].filter(Boolean);

        // Update currentSelection with full territorial data
        const newSelection: TerritorialSearchResult[] = [];
        const choices = this.choicesInstance.getValue();
        const choicesArray = Array.isArray(choices) ? choices : [choices];

        choicesArray.forEach((choice: any) => {
            if (choice?.customProperties?.territorialData) {
                newSelection.push(choice.customProperties.territorialData);
            }
        });
        this._currentSelection = newSelection;
    }
    protected afterRender(): void {
        if (this.readOnly) {
            return;
        }
        // Initialize UI elements after rendering
        this.ambitoSelect = this.roleElementSelector("ambito-select") as HTMLSelectElement;
        this.dropdownButton = this.roleElementSelector("dropdown-button") as HTMLButtonElement;

        // Initialize Choice.js
        this.initializeChoices();

        // Process initial value if present
        this.processInitialValue()
            .then(() => {
                this.valueChanged(this.value);
            })
            .catch(error => {
                console.error("Error processing initial value:", error);
            });
    }

    protected simplifyDataset(dataset: TerritorialSearchResult[]): any[] {
        const simplified: any[] = [];
        dataset.forEach(item => {
            switch (item.type) {
                case "nazione":

                    simplified.push({ type: item.type, name: item.name });
                    break;
                case "regione":
                    simplified.push({ type: item.type, name: item.region_name || item.name });
                    break;
                case "provincia":
                    simplified.push({ type: item.type, name: `<span class="fw-normal">Provincia:</span> ${item.province_name || item.name}` });
                    break;
                case "comune":
                    simplified.push({ type: item.type, name: `${item.name} (${item.province_sigla})` });
                    break;
                default:
                    break;
            }
        });
        return simplified;
    }
    private renderedOnce: boolean = false;
    protected render(): void {

        if (this.readOnly) {
            this.renderingData = this.buildRenderingData();
            this.getInitialDataset()
                .then((data) => {
                    this.renderingData = {
                        ...this.renderingData,
                        readonly: true,
                        dataset: this.simplifyDataset(data),
                        datasetLength: data && Array.isArray(data) ? data.length : 0,
                    }
                    const html = templateParse(this.templateId, this.renderingData);

                    if (html) {
                        this.innerHTML = html;
                        this.afterRender();
                    } else {
                        console.error(`Error rendering template '${this.templateId}' for ${this.tagName}`);
                        this.onRenderError();
                    }
                })
                .catch(error => {
                    console.error(`Error during initial dataset fetch: ${error}`);
                });
        }
        else {
            super.render();
            this.renderedOnce = true;
        }
    }
    protected valueChanged(newValue: string[]): void {
        // Dispatch event to notify of value change
        this.dispatch('ambito-value-changed', {
            value: newValue,
            selection: this.currentSelection
        });
    }
    protected onAttributeChange(name: string, oldValue: string, newValue: string): void {
        if (name === 'value' && this.renderedOnce) {
            this.initialValue = newValue;
            this.processInitialValue();
        }
    }
}